import clsx from 'clsx'
import React, { ButtonHTMLAttributes, FunctionComponent, ReactNode, useContext } from 'react'

import { InteractiveCell, InteractiveCellProps } from '../Cell'
import { Icon } from '../Icon'
import { SIZES } from '../Icon/constants'
import { Theme, useGetSelectedTheme, useThemeClass } from '../Theme'
import { AccordionContext } from './AccordionContext'
import { ACCORDION_BUTTON_DISPLAY_NAME } from './constants'
import styles from './style.module.scss'

export interface AccordionButtonProps {
  children: ReactNode
}

export const AccordionButton: FunctionComponent<AccordionButtonProps> = ({ children }) => {
  const { id, expanded, onHeaderClick, disabled, verticalAlignLabel, testNameSpace, animated, caret, accessoryPosition } = useContext(AccordionContext)
  const themeClass = useThemeClass()
  const selectedTheme = useGetSelectedTheme()

  const buttonClassNames = clsx(styles.toggle, disabled && styles.disabled, verticalAlignLabel && styles.verticalAlign, styles[themeClass])

  const defaultCaret: ReactNode = (
    <div className={clsx(styles.toggleIcon, expanded && styles.toggleIconExpanded, animated && styles.toggleIconAnimated)}>
      <Icon.CaretDown size={SIZES.EXTRA_SMALL} title='Expand' role='img' />
    </div>
  )

  const accessory = caret === null ? null : caret ?? defaultCaret

  const cellProps: InteractiveCellProps = {
    label: <>{children}</>,
    onClick: onHeaderClick,
    accessory,
    ...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-button` }),
    ...(disabled && { 'aria-disabled': disabled }),
    disabled,
    'aria-expanded': expanded,
    'aria-controls': `${id}-panel`,
    accessoryPosition: accessoryPosition ?? (verticalAlignLabel ? 'Center' : 'Top'),
  }

  const buttonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
    id,
    role: 'button',
    className: buttonClassNames,
    onClick: onHeaderClick,
    ...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-button` }),
    'aria-expanded': expanded,
    'aria-controls': `${id}-panel`,
    ...(disabled && { 'aria-disabled': disabled }),
    children: (
      <>
        <div className={styles.toggleContent}>{children}</div>
        {accessory}
      </>
    ),
  }

  const mapThemeToComponent: Record<Theme, React.ElementType> = {
    [Theme.AP]: 'button',
    [Theme.Cash]: 'button',
    [Theme.Pegasus]: InteractiveCell,
    [Theme.Kelpie]: InteractiveCell,
    [Theme.KelpieMint]: InteractiveCell,
  }

  const mapThemeToProps: Record<Theme, InteractiveCellProps | ButtonHTMLAttributes<HTMLButtonElement>> = {
    [Theme.AP]: buttonProps,
    [Theme.Cash]: buttonProps,
    [Theme.Pegasus]: cellProps,
    [Theme.Kelpie]: cellProps,
    [Theme.KelpieMint]: cellProps,
  }

  const Component = mapThemeToComponent[selectedTheme]
  const componentProps = mapThemeToProps[selectedTheme]

  return <Component {...componentProps} />
}

AccordionButton.displayName = ACCORDION_BUTTON_DISPLAY_NAME
